import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { IconClose, IconCheckCircle } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { useKpis } from '../../kpis/contexts/kpis';
import { SCHEDULE_TYPES, flattenSchedule, scheduleCounts } from '../../kpis/utils/schedule';
import PendingCalendar from '../../kpis/layouts/pending-calendar';
import CollapsibleGroup from '../../../common/components/CollapsibleGroup/CollapsibleGroup';
import ClientFarmSearch from '../../clients/forms/search';
import SectionTabs from '../../navigation/section-tabs';
import { PLANNING_TABS } from '../../navigation/tabs';

import './styles/planning.css';

// Planeación: AGENDA MENSUAL del hato (admin-kpis ?view=schedule&month=YYYY-MM · 0110).
// Tareas del mes (dx1/dx2/dx3+ y partos), hechas + pendientes, + arrastre de meses previos
// (overdue). Ancla en la tarea → incluye embriones externos y dx3+. Filtra por cliente/finca
// y por tipo (clic en las tarjetas). Navegar meses cambia el `month` consultado.

const MON = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MON_FULL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const RESULT = { positive: 'Positiva', negative: 'Negativa', doubtful: 'Dudosa', recheck: 'Rechequeo', ppl: 'PPL' };
const STATUS_OPTS = [['all', 'Todo'], ['overdue', 'Vencidas'], ['pending', 'Pendientes']];
const pad = (n) => String(n).padStart(2, '0');

function dayParts(iso) {
    const [, m, d] = (iso || '').split('-');
    const mi = Number(m) - 1;
    return { day: Number(d) || null, monAbbr: MON[mi] || '' };
}
function statusLabel(e) {
    if (!e.done) return null;
    if (e.type === 'births') return e.born_on ? `Parió · ${formatDateOnly(e.born_on)}` : 'Parió';
    return e.result ? `Hecha · ${RESULT[e.result] || e.result}` : 'Hecha';
}
function EventRow({ e }) {
    const done = statusLabel(e);
    const who = `${e.recipient?.number || '—'}${e.recipient?.name ? ` · ${e.recipient.name}` : ''}`;
    return (
        <li className={`plan-event plan-event--${e.tone}`}>
            {e.done && <span className="plan-event__check" title="Completada"><IconCheckCircle /></span>}
            <span className="plan-event__type" style={{ '--type-color': SCHEDULE_TYPES[e.type].color }}>{SCHEDULE_TYPES[e.type].label}</span>
            {/* En una tarea de DX, la vaca lleva al worklist de diagnóstico de SU OPU (/admin/dx?opu=),
                que es donde se registra el dx. Partos —y los dx sin OPU (embriones externos)— conservan
                el enlace a la ficha de la receptora. */}
            {e.type !== 'births' && e.opu?.id
                ? <Link to={`/admin/dx?opu=${e.opu.id}`} className="plan-event__who plan-event__who--link" title="Registrar diagnóstico (worklist de la OPU)">{who}</Link>
                : e.recipient?.id
                    ? <Link to={`/admin/recipients/${e.recipient.id}/stats`} className="plan-event__who plan-event__who--link" title="Ver receptora">{who}</Link>
                    : <span className="plan-event__who">{who}</span>}
            {e.date && <span className="plan-event__date">{formatDateOnly(e.date)}</span>}
            {(e.farm?.name || e.opu?.code || e.te_session?.code) && <span className="plan-event__ctx">{[e.farm?.name, e.opu?.code, e.te_session?.code].filter(Boolean).join(' · ')}</span>}
            {done ? <span className="plan-event__done">{done}</span> : (e.bucket === 'overdue' && <span className="plan-event__flag">vencida</span>)}
        </li>
    );
}

// Agrupa eventos por LOTE: (tipo de dx · finca · OPU · sesión TE). Todas las receptoras de un lote
// comparten la misma sesión TE y, por tanto, las mismas fechas nominales (TE / dx1 / dx2), que el
// backend adjunta en `te_session` → se pintan una vez en la cabecera del grupo, no por fila.
function groupByBatch(list) {
    const groups = new Map();
    for (const e of list) {
        const key = `${e.type}|${e.farm?.id ?? 'x'}|${e.opu?.id ?? 'x'}|${e.te_session?.id ?? 'x'}`;
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                badge: { label: SCHEDULE_TYPES[e.type].label, color: SCHEDULE_TYPES[e.type].color },
                farm: e.farm?.name || null,
                opu: e.opu?.code || null,
                te: e.te_session?.code || null,
                dates: { te: e.te_session?.date, dx1: e.te_session?.dx1_expected, dx2: e.te_session?.dx2_expected },
                items: [],
            });
        }
        groups.get(key).items.push(e);
    }
    return [...groups.values()];
}

// Lista de lotes plegables. `defaultOpen` abre los del mes (la agenda se sigue leyendo de corrido) y
// deja cerrado el arrastre, que puede traer cientos de tareas.
// El wrapper toma el rol que tenía `.plan-day__events` dentro del flex-row `.plan-day`
// (flex:1 + min-width:0). Sin él, cada grupo sería un flex-item suelto y se repartirían
// horizontalmente recortándose. El espaciado entre grupos lo da `.cgroup + .cgroup`.
function BatchGroups({ events, defaultOpen }) {
    return (
        <div className="plan-groups">
            {groupByBatch(events).map((g) => (
                <CollapsibleGroup
                    key={g.key}
                    badge={g.badge}
                    farm={g.farm}
                    opu={g.opu}
                    te={g.te}
                    dates={g.dates}
                    count={g.items.length}
                    defaultOpen={defaultOpen}
                >
                    <ul className="plan-day__events">
                        {g.items.map((e) => <EventRow key={e.id} e={e} />)}
                    </ul>
                </CollapsibleGroup>
            ))}
        </div>
    );
}

// `focus`: 'dx' (dx1/dx2/dx3+) | 'births' (partos). Dos tabs espejo, cada una enfocada en lo suyo.
export default function PlanningSession({ focus = 'births' }) {
    const [filters, setFilters] = useState({ client_id: '', farm_id: '' });
    const [active, setActive] = useState(null);
    const [selected, setSelected] = useState(null);   // día elegido
    const [typeFilter, setTypeFilter] = useState(null); // tipo elegido (clic en tarjeta)
    const [status, setStatus] = useState('all');       // all | overdue | pending
    const now = new Date();
    const [cal, setCal] = useState({ year: now.getFullYear(), month: now.getMonth() });

    const focusKeys = useMemo(() => (focus === 'dx' ? ['dx1', 'dx2', 'dx3plus'] : ['births']), [focus]);
    const month = `${cal.year}-${pad(cal.month + 1)}`;

    const pick = (item, type) => {
        if (type === 'farm') { setFilters({ client_id: '', farm_id: String(item.id) }); setActive({ type, label: `${item.name}${item.owner?.name ? ` · ${item.owner.name}` : ''}` }); }
        else { setFilters({ client_id: String(item.id), farm_id: '' }); setActive({ type, label: item.name }); }
    };
    const clearFilter = () => { setFilters({ client_id: '', farm_id: '' }); setActive(null); };
    const shiftMonth = (delta) => { setSelected(null); setCal((c) => { const d = new Date(c.year, c.month + delta, 1); return { year: d.getFullYear(), month: d.getMonth() }; }); };

    const { data, isLoading, isFetching, error } = useKpis('schedule', { ...filters, month });
    const counts = useMemo(() => scheduleCounts(data?.schedule || {}), [data]);
    const allEvents = useMemo(() => flattenSchedule(data?.schedule || {}, 'scheduled').filter((e) => focusKeys.includes(e.type)), [data, focusKeys]);
    const overdue = useMemo(() => flattenSchedule(data?.overdue || {}, 'overdue').filter((e) => focusKeys.includes(e.type)).sort((a, b) => String(a.date).localeCompare(String(b.date))), [data, focusKeys]);

    const events = useMemo(() => {
        let list = typeFilter ? allEvents.filter((e) => e.type === typeFilter) : allEvents;
        if (status === 'overdue') list = list.filter((e) => !e.done && e.tone === 'overdue');
        else if (status === 'pending') list = list.filter((e) => !e.done && e.tone !== 'overdue');
        return list;
    }, [allEvents, typeFilter, status]);
    const overdueShown = useMemo(() => (typeFilter ? overdue.filter((e) => e.type === typeFilter) : overdue), [overdue, typeFilter]);

    // Agenda del mes agrupada por día (filtrada al día elegido si hay).
    const days = useMemo(() => {
        const byDate = new Map();
        events.forEach((e) => { const d = e.date || 'zzz'; if (!byDate.has(d)) byDate.set(d, []); byDate.get(d).push(e); });
        return [...byDate.entries()]
            .filter(([date]) => !selected || date === selected)
            .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
            .map(([date, evs]) => ({ date: date === 'zzz' ? null : date, events: evs.sort((x, y) => x.type.localeCompare(y.type)) }));
    }, [events, selected]);

    return (
        <div className="plan">
            <SectionTabs tabs={PLANNING_TABS} />
            <header className="plan__header">
                <div className="plan__heading">
                    <h1 className="plan__title">{focus === 'dx' ? 'Agenda de diagnósticos' : 'Agenda de partos'}</h1>
                    <p className="plan__subtitle">{focus === 'dx' ? 'Palpaciones dx1/dx2/dx3+ del mes' : 'Partos previstos del mes'} — hechos y pendientes — más el arrastre vencido.</p>
                </div>
                <div className="plan__search">
                    {active ? (
                        <div className="plan-active">
                            <span className="plan-active__label">{active.type === 'farm' ? 'Finca' : 'Cliente'}</span>
                            <span className="plan-active__value">{active.label}</span>
                            <button type="button" className="plan-active__clear" onClick={clearFilter} aria-label="Quitar filtro"><IconClose /></button>
                        </div>
                    ) : (
                        <ClientFarmSearch scope="all" placeholder="Filtrar por cliente o finca…" onSelect={pick} />
                    )}
                </div>
            </header>

            {!isLoading && !error && (
                <div className="plan-kpis">
                    {focusKeys.map((k) => {
                        const c = counts[k] || { total: 0, done: 0, pending: 0 };
                        const on = typeFilter === k;
                        return (
                            <button key={k} type="button" className={`plan-kpi plan-kpi--btn ${on ? 'plan-kpi--on' : ''}`} style={{ '--type-color': SCHEDULE_TYPES[k].color }} onClick={() => setTypeFilter(on ? null : k)}>
                                <span className="plan-kpi__value">{c.total}</span>
                                <span className="plan-kpi__label">{SCHEDULE_TYPES[k].label}</span>
                                {c.total > 0 && <span className="plan-kpi__sub">{c.done} hechas · {c.pending} pend.</span>}
                            </button>
                        );
                    })}
                    {focusKeys.length > 1 && (
                        <div className="plan-kpi plan-kpi--total">
                            <span className="plan-kpi__value">{focusKeys.reduce((s, k) => s + (counts[k]?.total || 0), 0)}</span>
                            <span className="plan-kpi__label">Total mes</span>
                            {overdue.length > 0 && <span className="plan-kpi__overdue">{overdue.length} vencida(s)</span>}
                        </div>
                    )}
                </div>
            )}

            <div className="plan-status" role="tablist" aria-label="Filtro de estado">
                {STATUS_OPTS.map(([v, l]) => (
                    <button key={v} type="button" role="tab" aria-selected={status === v} className={`plan-status__btn ${status === v ? 'plan-status__btn--on' : ''}`} onClick={() => setStatus(v)}>{l}</button>
                ))}
            </div>

            {error && <div className="plan__error">{error.status === 400 ? 'Mes inválido.' : (error.message || 'No se pudo cargar la agenda')}</div>}

            {isLoading ? (
                <div className="plan__state">Cargando agenda…</div>
            ) : (
                <div className={`plan__main ${isFetching ? 'plan__main--busy' : ''}`}>
                    <div className="plan__calendar">
                        <PendingCalendar events={events} year={cal.year} month={cal.month} onMonthChange={shiftMonth} selectedDate={selected} onSelectDay={setSelected} types={SCHEDULE_TYPES} typeKeys={focusKeys} />
                        <p className="plan__note">Agenda del mes elegido. Navegá los meses con las flechas; elegí un día para filtrar la lista. Las tarjetas filtran por tipo.</p>
                    </div>

                    <div className="plan__agenda">
                        {selected && (
                            <div className="plan-agenda__filter">
                                <span>Día: <strong>{formatDateOnly(selected)}</strong></span>
                                <button type="button" className="plan-agenda__clear" onClick={() => setSelected(null)}>Ver todo el mes</button>
                            </div>
                        )}

                        <h2 className="plan__month">{MON_FULL[cal.month]} {cal.year}</h2>
                        {!days.length ? (
                            <div className="plan__state">{selected ? 'Sin tareas ese día.' : 'Sin tareas programadas este mes.'}</div>
                        ) : (
                            days.map((day) => {
                                const p = dayParts(day.date);
                                return (
                                    <Fragment key={day.date || 'sin-fecha'}>
                                        <div className="plan-day">
                                            <div className="plan-day__date">
                                                <span className="plan-day__num">{p.day ?? '—'}</span>
                                                <span className="plan-day__mon">{p.monAbbr}</span>
                                            </div>
                                            <BatchGroups events={day.events} defaultOpen />
                                        </div>
                                    </Fragment>
                                );
                            })
                        )}

                        {!selected && status !== 'pending' && overdueShown.length > 0 && (
                            <div className="plan-overdue">
                                <h2 className="plan__month plan__month--overdue">Arrastre (vencidas de meses previos) · {overdueShown.length}</h2>
                                <BatchGroups events={overdueShown} defaultOpen={false} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
